/**
 * Asset Scanner Service
 * Requirements: 4.1, 4.2, 4.3, 4.4
 *
 * Scans course content for all media asset references and generates
 * an AssetManifest listing all external media URLs.
 */

import type {
  ExportedCourse,
  CourseBlock,
  VideoBlock,
  AssetManifest,
  AssetEntry,
} from '@course-authoring/shared-types';

/**
 * Infers the content type from a URL based on file extension or known patterns.
 *
 * @param url - The asset URL
 * @param category - The asset category hint
 * @returns The inferred MIME content type
 */
function inferContentType(
  url: string,
  category: 'video' | 'image' | 'caption'
): AssetEntry['contentType'] {
  const lowerUrl = url.toLowerCase();

  if (category === 'caption') {
    return 'text/vtt';
  }

  if (category === 'video') {
    if (lowerUrl.includes('.m3u8') || lowerUrl.includes('m3u8')) {
      return 'application/x-mpegURL';
    }
    if (lowerUrl.includes('.webm')) {
      return 'video/webm';
    }
    // Default to mp4 for video
    return 'video/mp4';
  }

  // Image category
  if (lowerUrl.includes('.png')) {
    return 'image/png';
  }
  if (lowerUrl.includes('.webp')) {
    return 'image/webp';
  }
  if (lowerUrl.includes('.gif')) {
    return 'image/gif';
  }
  // Default to jpeg for images
  return 'image/jpeg';
}

/**
 * Generates a unique ID for an asset based on its URL.
 * Uses a simple hash of the URL to create a deterministic ID.
 *
 * @param url - The asset URL
 * @returns A unique identifier string
 */
function generateAssetId(url: string): string {
  // Simple hash function for deterministic ID generation
  let hash = 0;
  for (let i = 0; i < url.length; i++) {
    const char = url.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return `asset-${Math.abs(hash).toString(16)}`;
}

/**
 * Extracts assets from a video block.
 *
 * @param block - The video block to scan
 * @returns Array of asset entries found in the block
 */
function extractVideoAssets(block: VideoBlock): Array<{ url: string; category: 'video' | 'image' | 'caption'; blockId: string }> {
  const assets: Array<{ url: string; category: 'video' | 'image' | 'caption'; blockId: string }> = [];

  // Main video URL
  if (block.url) {
    assets.push({ url: block.url, category: 'video', blockId: block.id });
  }

  // Poster image URL
  if (block.posterUrl) {
    assets.push({ url: block.posterUrl, category: 'image', blockId: block.id });
  }

  // Track URLs (captions, subtitles, chapters)
  if (block.tracks && Array.isArray(block.tracks)) {
    for (const track of block.tracks) {
      if (track.src) {
        assets.push({ url: track.src, category: 'caption', blockId: block.id });
      }
    }
  }

  return assets;
}

function imageAsset(url: string | undefined, blockId: string): { url: string; category: 'image'; blockId: string }[] {
  return url ? [{ url, category: 'image', blockId }] : [];
}

function videoAsset(url: string | undefined, blockId: string): { url: string; category: 'video'; blockId: string }[] {
  return url ? [{ url, category: 'video', blockId }] : [];
}

/**
 * Extracts all asset references from a single block.
 *
 * @param block - The course block to scan
 * @returns Array of raw asset data found in the block
 */
function extractAssetsFromBlock(block: CourseBlock): Array<{ url: string; category: 'video' | 'image' | 'caption'; blockId: string }> {
  switch (block.type) {
    case 'video':
      return extractVideoAssets(block);
    case 'basic-content-a':
    case 'basic-content-b':
    case 'lesson-complete':
      return imageAsset(block.imageUrl, block.id);
    case 'comparison-slider':
      return [
        ...imageAsset(block.option1.imageUrl, block.id),
        ...imageAsset(block.option2.imageUrl, block.id),
        ...videoAsset(block.option1.videoUrl, block.id),
        ...videoAsset(block.option2.videoUrl, block.id),
        ...imageAsset(block.option1.videoPosterUrl, block.id),
        ...imageAsset(block.option2.videoPosterUrl, block.id),
      ];
    case 'click-expand':
      return block.topics.flatMap((topic) => imageAsset(topic.imageUrl, block.id));
    case 'categorization':
      return [
        ...block.categories.flatMap((category) =>
          category.icon?.type === 'image' ? imageAsset(category.icon.value, block.id) : []
        ),
        ...block.items.flatMap((item) =>
          item.icon?.type === 'image' ? imageAsset(item.icon.value, block.id) : []
        ),
      ];
    default:
      return [];
  }
}

/**
 * Scans an exported course and generates an AssetManifest containing
 * all external media references.
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4
 *
 * @param course - The exported course to scan
 * @returns AssetManifest with all asset references
 */
export function scanAssets(course: ExportedCourse): AssetManifest {
  // Map to track unique assets by URL and aggregate block references
  const assetMap = new Map<string, {
    url: string;
    category: 'video' | 'image' | 'caption';
    referencedBy: Set<string>;
  }>();

  // Scan all blocks for assets
  for (const block of course.blocks) {
    const blockAssets = extractAssetsFromBlock(block);

    for (const asset of blockAssets) {
      const existing = assetMap.get(asset.url);
      if (existing) {
        // Add block reference to existing asset
        existing.referencedBy.add(asset.blockId);
      } else {
        // Create new asset entry
        assetMap.set(asset.url, {
          url: asset.url,
          category: asset.category,
          referencedBy: new Set([asset.blockId]),
        });
      }
    }
  }

  // Convert map to AssetEntry array
  const assets: AssetEntry[] = Array.from(assetMap.values()).map((asset) => ({
    id: generateAssetId(asset.url),
    url: asset.url,
    contentType: inferContentType(asset.url, asset.category),
    category: asset.category,
    referencedBy: Array.from(asset.referencedBy),
    bundled: false,
  }));

  return {
    assets,
    totalCount: assets.length,
    generatedAt: new Date().toISOString(),
  };
}

export function scanAssetsForCourses(courses: readonly ExportedCourse[]): AssetManifest {
  const manifests = courses.map(scanAssets);
  const assetsByUrl = new Map<string, AssetEntry>();

  for (const manifest of manifests) {
    for (const asset of manifest.assets) {
      const existing = assetsByUrl.get(asset.url);
      if (!existing) {
        assetsByUrl.set(asset.url, { ...asset, referencedBy: [...asset.referencedBy] });
        continue;
      }
      existing.referencedBy = Array.from(new Set([...existing.referencedBy, ...asset.referencedBy]));
    }
  }

  const assets = Array.from(assetsByUrl.values());
  return {
    assets,
    totalCount: assets.length,
    generatedAt: new Date().toISOString(),
  };
}

/**
 * Bytes for an asset bundled into the export ZIP.
 */
export interface BundledAssetBytes {
  bytes: ArrayBuffer;
  contentType: string;
  packagedPath: string;
  sha256: string;
}

/**
 * Result of fetching bundleable assets.
 *
 * `bundled` is keyed by the original asset URL so the bundler can rewrite
 * `data/course.json` URLs in a single pass.
 */
export interface FetchBundleableAssetsResult {
  bundled: Map<string, BundledAssetBytes>;
  errors: import('@course-authoring/shared-types').ExportError[];
}

const EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'text/vtt': 'vtt',
};

function extensionFor(contentType: string): string {
  return EXTENSION_BY_CONTENT_TYPE[contentType] ?? 'bin';
}

async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const view = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < view.length; i++) {
    hex += view[i]!.toString(16).padStart(2, '0');
  }
  return hex;
}

/**
 * Fetches bytes for packageable assets so the bundler can embed them in the
 * ZIP. Package exports no longer call this for course media; keep it only for
 * legacy/internal flows that explicitly need package-local bytes. HLS playlists
 * are reported explicitly because bundling the playlist without every segment
 * would not produce a self-contained package.
 *
 * `strictness: 'strict'` (used for Published source) returns errors for any
 * fetch failure; `'lenient'` (Draft) lets the bundler proceed with the asset
 * left referenced.
 */
export async function fetchBundleableAssets(
  manifest: AssetManifest,
  options: { strictness: 'strict' | 'lenient' }
): Promise<FetchBundleableAssetsResult> {
  const bundled = new Map<string, BundledAssetBytes>();
  const errors: import('@course-authoring/shared-types').ExportError[] = [];
  const code = options.strictness === 'strict' ? 'ASSET_BUNDLE_FAILED' : 'ASSET_UNREACHABLE';

  await Promise.all(
    manifest.assets
      .map(async (asset) => {
        if (asset.contentType === 'application/x-mpegURL') {
          errors.push({
            code,
            message: `HLS video streams cannot be bundled into an internal export yet: ${asset.url}`,
            details: { url: asset.url, contentType: asset.contentType },
          });
          return;
        }

        try {
          const response = await fetch(asset.url, { method: 'GET' });
          if (!response.ok) {
            errors.push({
              code,
              message: `Failed to fetch ${asset.category}: ${asset.url}`,
              details: { url: asset.url, status: response.status },
            });
            return;
          }

          const bytes = await response.arrayBuffer();
          const sha = await sha256Hex(bytes);
          const ext = extensionFor(asset.contentType);
          const packagedPath = `assets/${asset.category}/${sha.slice(0, 16)}.${ext}`;

          bundled.set(asset.url, {
            bytes,
            contentType: asset.contentType,
            packagedPath,
            sha256: sha,
          });
        } catch (error) {
          errors.push({
            code,
            message: `Failed to fetch ${asset.category}: ${asset.url}`,
            details: {
              url: asset.url,
              error: error instanceof Error ? error.message : String(error),
            },
          });
        }
      })
  );

  return { bundled, errors };
}
